/**
 * 表示言語の決定と文言解決。
 *
 * 方式は backend（`app/core/i18n.py`）と同じ＝**日本語の原文をそのままキー**にして
 * EN 辞書を引き、未訳は原文へフォールバックする。原文がキーなので、辞書に足し忘れても
 * 画面が空欄やキー名になることは無い。
 *
 * 🔑 言語はブラウザの設定ではなく **Shopify 管理画面の表示言語**に合わせる。
 * admin が埋め込み URL に付ける `locale` を使う。課金復帰では認証済み操作の保存値を再取得する。決めた値は
 * `X-DenchoBox-Locale` ヘッダで backend にも渡す（サーバ側のエラー文言を画面と
 * 同じ言語で出すため）。
 */

export type Locale = "ja" | "en";

const EN: Record<string, string> = {
  "課金の状態を確認しています": "Checking your billing status",
  "課金の復帰情報を確認できませんでした": "Your billing return context could not be confirmed",
  "課金の状態を確認できませんでした": "Your billing status could not be confirmed",
  "課金の操作はまだ完了していません": "Your billing operation is still pending",
  "課金の状態を確認しました": "Your billing status has been confirmed",
  "課金の操作は終了しました": "Your billing operation has ended",
  "保存された操作を再確認してください。新しい購読は開始しません。": "Check the saved operation. A new subscription will not be started.",
  "Shopify管理画面から開き直すか、この店舗で保存された課金操作を確認してください。": "Reopen the app from Shopify admin or check this store's saved billing operation.",
  "保存された課金操作を確認": "Check saved billing operation",
  "保存された課金操作はありません": "There is no saved billing operation",
  "料金プランへ戻る": "Return to plans",
  "続ける": "Continue",
  "Shopifyで承認を続ける": "Continue approval in Shopify",
  "埋め込み情報を確認できません。Shopify管理画面からアプリを開き直してください。": "The embedded context is unavailable. Reopen the app from Shopify admin.",
  "課金操作の保存を確認できませんでした。保存された操作を確認してください。": "The billing operation could not be confirmed. Check the saved operation.",
  "Shopifyとの接続を確認しています": "Checking your Shopify connection",
  "接続を確認中": "Checking connection",
  "接続状態を確認してください": "Check your connection status",
  "この画面を保ったまま、店舗との接続を確認しています。": "We are connecting your store while keeping this screen open.",
  "接続の結果を確認できません。新しい接続は開始せず、保存された状態を再確認してください。": "The connection result is unknown. Check the saved status before starting another connection.",
  "接続を完了できませんでした。状態を再確認してから接続をやり直せます。": "The connection could not be completed. Check the status before trying again.",
  "接続の有効期限が切れました。状態を再確認してから接続をやり直せます。": "The connection has expired. Check the status before trying again.",
  "別の接続操作に切り替わりました。現在の状態を再確認してください。": "Another connection operation has replaced this one. Check the current status.",
  "別の担当者または更新処理が接続を進めています。完了後に状態を再確認してください。": "Another staff member or an update is handling the connection. Check again after it finishes.",
  "Shopifyの認証を確認できません。Shopify管理画面からアプリを開き直してください。": "Shopify authentication is unavailable. Reopen the app from Shopify admin.",
  "以前のデータを削除しています。完了するまで接続を開始できません。": "Previous data is being deleted. A connection can start after deletion is complete.",
  "応答を確認できませんでした。操作を繰り返さず、保存された状態を再確認してください。": "The response could not be confirmed. Check the saved status before repeating the action.",
  "接続を処理しています。自動確認を停止しました。しばらくしてから状態を再確認してください。": "The connection is still processing. Automatic checks have stopped. Check the status again shortly.",
  "保存された接続操作はありません。接続を開始できます。": "There is no saved connection operation. You can start connecting.",
  "この画面から接続を開始できません。Shopify管理画面からアプリを開き直してください。": "A connection cannot start from this page. Reopen the app from Shopify admin.",
  "接続後の店舗情報を確認できません。保存された状態を再確認してください。": "The store could not be verified after connecting. Check the saved status.",
  "保存された接続操作を続けられます。": "You can continue the saved connection operation.",
  "接続を開始": "Start connection",
  "接続を続ける": "Continue connection",
  "店舗の接続を回復してください": "Restore the store connection",
  "接続を回復": "Restore connection",
  "保存済みデータは閲覧できます。注文の取得などは、新しい接続を確認するまで停止しています。": "You can view saved data. Order retrieval is paused until a new connection is confirmed.",
  "接続をやり直す": "Try connecting again",
  "再インストール後の接続を確認": "Check connection after reinstalling",
  "Shopifyで再インストールを済ませた場合だけ、接続を確認できます。": "Use this check only after reinstalling the app in Shopify.",
  "保存された接続は終了しています。現在の店舗との接続を確認できます。": "The saved connection has ended. You can check the connection for the current store.",
  "注文取込の準備状況を確認してください": "Check order import preparation",
  "接続済み・注文取込の準備中": "Connected — preparing order import",
  "店舗との接続は完了しました。注文取込の準備状況はまだ確認できていません。": "Your store is connected. Order import preparation has not been confirmed yet.",
  "店舗との接続は完了しました。注文の取込準備は別に進むため、まだ注文が表示されない場合があります。": "Your store is connected. Order import is prepared separately, so orders may not appear yet.",
  ホーム: "Home",
  "電子帳簿保存法アーカイブと適格請求書の発行状況": "Archive and invoice usage",
  プラン: "Plan",
  ストア: "Store",
  トライアル: "Trial",
  "残り{days}日": "{days} days left",
  未使用: "Not started",
  使い切り: "Used",
  "今月のアーカイブ": "Archived this month",
  "今月の発行": "Issued this month",
  "{count} 件": "{count} items",
  "{count} 枚": "{count} documents",
  "上限 {limit} 件": "Limit {limit} items",
  "上限 {limit} 枚": "Limit {limit} documents",
  無制限: "Unlimited",
  "上限を超えていますが、保存は止まっていません":
    "Over the limit, but archiving has not stopped",
  "電子帳簿保存法は全件保存が義務のため、上限を超えても取り込みは続きます。まとめて取り出す際に {plan} 以上のプランが必要です。":
    "Archiving continues past the limit because the law requires every transaction to be stored. Bulk export requires the {plan} plan or higher.",
  "上限に近づいています": "Approaching the limit",
  "上限に達しています。次の1枚は発行できません":
    "Limit reached. The next document cannot be issued",
  "{plan} 以上のプランで発行を再開できます":
    "Upgrade to {plan} or higher to resume issuing",
  "読み込みに失敗しました": "Failed to load",
  "旧データの削除を待っています": "Waiting for previous data to be deleted",
  "状態を再確認": "Check status again",
  "削除が完了するまで、以前のデータや設定を再利用せずに待機します。完了後は再度インストール手続きを行ってください。":
    "Your previous data and settings will remain unavailable until deletion is complete. Then start a new installation.",
  再読み込み: "Reload",
  "アプリの読み込みに失敗しました": "The app failed to load",
  "アーカイブを検索": "Search archive",
  アーカイブ検索: "Archive search",
  "取引年月日・取引金額・取引先名を組み合わせて検索できます":
    "Search by any combination of transaction date, amount, and counterparty",
  "処理に失敗しました": "Action failed",
  検索条件: "Search filters",
  "取引年月日（開始）": "Transaction date (from)",
  "取引年月日（終了）": "Transaction date (to)",
  "取引金額（下限）": "Amount (minimum)",
  "取引金額（上限）": "Amount (maximum)",
  取引先名: "Counterparty",
  "部分一致で検索します": "Matches any part of the name",
  検索: "Search",
  "条件をクリア": "Clear filters",
  検索結果: "Results",
  "{count}件が見つかりました": "{count} documents found",
  CSV索引簿: "CSV index",
  一括ZIP: "Bulk ZIP",
  "証跡パック（Pro）": "Evidence pack (Pro)",
  "証跡パックには訂正前を含む全版・監査履歴・整合性検証が入ります":
    "The evidence pack includes every version, the audit history, and integrity verification.",
  "変更した検索条件を反映してから出力してください":
    "Apply the changed search filters before exporting",
  "変更した検索条件は、検索ボタンで反映してから出力できます":
    "Apply the changed filters with Search before exporting",
  "検索結果を読み込めませんでした": "Failed to load search results",
  "条件に一致する書類はありません": "No documents match these filters",
  "{from}〜{to}件 / 全{total}件": "{from}–{to} of {total}",
  取引年月日: "Transaction date",
  書類: "Document",
  取引金額: "Amount",
  状態: "Status",
  操作: "Actions",
  現行版: "Current",
  旧版: "Previous",
  プレビュー: "Preview",
  ダウンロード: "Download",
  "保存日時: {date}": "Archived: {date}",
  改ざん検知: "Verify integrity",
  閉じる: "Close",
  "保存時の内容と一致しています": "Content matches the archived copy",
  "保存後の変更を検知しました": "A change after archiving was detected",
  "保存時: {expected} / 現在: {actual}":
    "Archived: {expected} / Current: {actual}",
  書類プレビュー: "Document preview",
  "この形式はブラウザ内で表示できません": "This format cannot be previewed in the browser",
  "ダウンロードして内容を確認してください": "Download the document to inspect it",
  "版の履歴（新しい順）": "Version history (newest first)",
  保存日時: "Archived at",
  表示中: "Viewing",
  表示: "View",
  DL: "DL",
  料金プラン: "Plans",
  "店舗の利用量に合うプランを選択できます":
    "Choose the plan that fits your store's usage",
  ホームへ戻る: "Back to home",
  "料金プランを読み込んでいます": "Loading plans",
  "料金プランを読み込めませんでした": "Failed to load plans",
  "{plan}以上のプランが必要です": "The {plan} plan or higher is required",
  "この操作を続けるにはプランを選び、Shopifyの承認画面で内容を確認してください。":
    "To continue, choose a plan and review it on Shopify's approval page.",
  "有料プランは選択後にShopifyの承認画面へ移動します。承認するまで課金は始まりません。":
    "After selecting a paid plan, you will review it on Shopify's approval page. Billing does not begin until you approve it.",
  "アーカイブ利用枠を超えても、保存・検索・単票のプレビューとダウンロードは止まりません。":
    "Archiving, search, and individual preview and download continue even after the archive allowance is exceeded.",
  "有料プランは日本円と米ドルの価格を併記しています。実際の請求通貨と金額はShopifyの承認画面で確認してください。":
    "Paid plans show both JPY and USD prices. Confirm the actual billing currency and amount on Shopify's approval page.",
  "プラン変更に失敗しました": "Failed to change plan",
  "利用できる料金プランがありません": "No plans are currently available",
  無料: "Free",
  おすすめ: "Recommended",
  必要な最小プラン: "Minimum required",
  利用中: "Current",
  " / 月": " / month",
  "初回{days}日間無料": "First {days} days free",
  "初回{days}日間無料（1店舗につき1回）": "First {days} days free (once per store)",
  "無料期間 残り{days}日": "{days} free trial days remaining",
  "✓ {feature}": "✓ {feature}",
  "アーカイブ利用枠 月10注文": "Archive allowance: 10 orders/month",
  "アーカイブ利用枠 月100注文": "Archive allowance: 100 orders/month",
  "アーカイブ利用枠 月1,000注文": "Archive allowance: 1,000 orders/month",
  "請求書・領収書 月10枚": "Invoices and receipts: 10/month",
  "請求書・領収書 月100枚": "Invoices and receipts: 100/month",
  "請求書・領収書 無制限": "Unlimited invoices and receipts",
  "検索・単票のプレビューとダウンロード":
    "Search, preview, and download individual documents",
  "月間利用枠内のCSV索引簿・一括ZIP":
    "CSV index and bulk ZIP within the monthly allowance",
  "CSV索引簿・一括ZIP": "CSV index and bulk ZIP",
  "検証付き証跡パック（開発中）": "Verified evidence pack (in development)",
  "アーカイブ・請求書 無制限": "Unlimited archive allowance and invoices",
  "優先サポート（予約制）": "Priority support by appointment",
  "東京リージョン保管・複数ストアは個別相談":
    "Contact us about Tokyo-region storage and multiple stores",
  "提供条件は相談後に確認": "Confirm availability and terms after consultation",
  "セルフサービス購入は現在受け付けていません":
    "Self-service purchase is not currently available",
  要相談: "Contact us",
  "参考価格です。実際の提供条件は相談後に確認します。":
    "This is a reference price. Actual availability and terms are confirmed after consultation.",
  Enterpriseについて相談: "Contact us about Enterprise",
  "Freeプランへ変更しますか？": "Change to the Free plan?",
  "有料購読を解約してFreeへ変更": "Cancel paid subscription and change to Free",
  キャンセル: "Cancel",
  "Freeへ変更すると、現在の有料購読をShopify上で解約します。Freeの利用枠に変わることを確認してから続けてください。":
    "Changing to Free cancels your current paid subscription on Shopify. Continue only after confirming the Free plan allowances.",
  現在のプラン: "Current plan",
  Freeへ変更: "Change to Free",
  "{plan}を{days}日間無料で試す": "Try {plan} free for {days} days",
  "{plan}を選ぶ": "Choose {plan}",
  "料金プランを見る": "View plans",
  "帳票を作成": "Create document",
  "請求書・領収書を作成": "Create invoice or receipt",
  "注文を特定できませんでした": "The order could not be identified",
  "発行前の内容を読み込めませんでした": "Failed to load the document preview",
  "保存済み注文から作成します": "Create from an archived order",
  "税率別の発行内容": "Amounts by tax rate",
  "発行前の確認です。この表示だけでは枚数を消費しません。":
    "Review before issuing. Viewing this page does not use your document allowance.",
  税率: "Tax rate",
  税抜: "Net",
  消費税: "Tax",
  税込: "Gross",
  合計: "Total",
  "今月 {count}枚 / 無制限": "{count} issued this month / Unlimited",
  "今月 {count} / {limit}枚": "{count} / {limit} issued this month",
  発行者情報: "Issuer details",
  "PDFへ印字する情報です。請求書には名称と登録番号が必要です。":
    "These details are printed on the PDF. An issuer name and registration number are required for an invoice.",
  "請求書を発行する前に設定が必要です": "Complete the issuer details before issuing an invoice",
  発行者名: "Issuer name",
  "登録番号（T+13桁）": "Registration number (T + 13 digits)",
  郵便番号: "Postal code",
  電話番号: "Phone number",
  住所: "Address",
  発行者情報を保存: "Save issuer details",
  "発行者情報を保存しました": "Issuer details saved",
  "今月の発行上限に達しています": "The monthly issuing limit has been reached",
  "新しい発行は停止しますが、発行済み書類の取得と訂正版の再発行は続けられます。":
    "New documents are paused, but you can still download issued documents and reissue corrections.",
  適格請求書: "Qualified invoice",
  領収書: "Receipt",
  "注文の訂正あり": "Order amended",
  発行済み: "Issued",
  未発行: "Not issued",
  "税率ごとの対価と消費税額、登録番号を記載します。":
    "Includes amounts and tax by rate, plus the issuer registration number.",
  "入金の受領を示すPDFを作成します。登録番号がある場合は併記します。":
    "Creates a PDF recording receipt of payment and includes the registration number when available.",
  "発行日時: {date}": "Issued: {date}",
  "発行後に元の注文が訂正されています。内容を確認してから修正版を再発行してください。":
    "The source order was amended after this document was issued. Review the amounts before reissuing a corrected version.",
  PDFを取得: "Download PDF",
  修正版を再発行: "Reissue correction",
  "{kind}を発行": "Issue {kind}",
  "修正版を発行し、アーカイブへ保存しました":
    "The corrected document was issued and archived",
  "発行済みの書類を表示しました": "The existing issued document is available",
  "書類を発行し、アーカイブへ保存しました": "The document was issued and archived",
  ストア表示を診断: "Review store disclosures",
  "販売ページや特商法表記の本文を貼り付けて確認します":
    "Paste sales-page or commerce-disclosure copy for review",
  診断できる範囲: "What this review covers",
  "この診断は補助です。URLを巡回せず、ここへ貼り付けた本文だけを確認します。最終的な法令適合性の判断はユーザーの責任です。":
    "This review is an aid. Only the text pasted here is reviewed; no URLs are crawled. You are responsible for the final judgment of legal compliance.",
  ページ種別: "Page type",
  "商品・サービス販売": "Products or services",
  "ソフトウェア・アプリ販売": "Software or apps",
  確認する本文: "Text to review",
  "公開予定の特商法表記、商品説明、口コミ・体験談を含む本文を貼り付けてください。":
    "Paste the commerce disclosure, product copy, testimonials, or reviews you plan to publish.",
  本文を診断: "Review text",
  診断に失敗しました: "Review failed",
  診断結果は確認補助です: "This result is a review aid",
  "この診断は表示確認の補助です。最終的な法令適合性の判断はユーザーの責任です。個別の法的判断や法令適合性を保証するものではありません。最終確認は必要に応じて専門家へ依頼してください。":
    "This review is an aid for checking disclosures. You are responsible for the final judgment of legal compliance. It does not guarantee case-specific legal conclusions or legal compliance. Consult a qualified professional for final review if needed.",
  確認結果: "Review results",
  "ルール版: {version} / 入力ID: {hash}": "Rule version: {version} / Input ID: {hash}",
  "確認できた {count}": "Present {count}",
  "不足候補 {count}": "Possible omissions {count}",
  "人の確認 {count}": "Human review {count}",
  区分: "Category",
  項目: "Item",
  判定: "Status",
  根拠と修正案: "Evidence and recommendation",
  "Proで根拠と修正案を確認できます": "See evidence and recommendations on Pro",
  確認できた: "Present",
  不足候補: "Possible omission",
  人の確認が必要: "Human review required",
  Freeでは優先3件を表示しています: "Free shows the top three items",
  "全結果では、ほかに {count}件の確認項目があります。":
    "The full result contains {count} additional review items.",
};

let current: Locale = "ja";
const localeListeners = new Set<() => void>();

/** `?locale=` から表示言語を決めて保持する。en 系以外はすべて ja（backend と同じ規則）。 */
export function initLocale(search: string): Locale {
  const raw = new URLSearchParams(search).get("locale") ?? "";
  const next = raw.trim().toLowerCase().startsWith("en") ? "en" : "ja";
  if (next !== current) {
    current = next;
    localeListeners.forEach((listener) => listener());
  }
  return current;
}

/** 保存した復帰文脈による言語変更を、既存の画面を保ったまま通知する。 */
export function subscribeLocale(listener: () => void): () => void {
  localeListeners.add(listener);
  return () => { localeListeners.delete(listener); };
}

export function currentLocale(): Locale {
  return current;
}

/**
 * 日本語の原文を表示言語へ解決する。`params` は原文中の `{name}` を差し込む。
 *
 * 数や名前を挟む文は**断片に割らない**（英語は語順が違うので並べ直すと訳が壊れる）
 * ＝1つの原文をキーにして丸ごと訳す。backend の `t()` と同じ約束。
 */
export function t(ja: string, params?: Record<string, string | number>): string {
  const text = current === "en" ? (EN[ja] ?? ja) : ja;
  if (!params) return text;
  return Object.entries(params).reduce(
    (acc, [name, value]) => acc.replaceAll(`{${name}}`, String(value)),
    text,
  );
}
